import { CircularProgressOverlay } from '@campfire/circular-progress-overlay';
import { encodeDate, unpackToTime } from '@campfire/hot-date';
import { Box, Typography, TableRow, TableCell } from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/styles';
import { DateTime } from 'luxon';
import MUIDataTable, { MUIDataTableOptions } from 'mui-datatables';
import React, { useEffect, useMemo, useState } from 'react';
import { useCampfireQuery } from '../../../../global/network/useCampfireQuery';
import { ImpactDashboardFooter } from '../general-impact-dashboard/ImpactDashboardFooter';
import {
  getTableColumns,
  getTableData,
  getFieldDisplayValue,
  IndividualSessionReportData,
  CICOData,
} from './impact-dashboard-detailed-model';
import { FallbackAvatar } from '../../user-profile/components/Avatar';
import { GET_IMPACT_DASHBOARD_DETAILED_VIEW } from './impact-dashboard-detailed-view-model.gql';
import {
  ImpactDashboardDetailedDateFilter,
  impactDashboardDetailedInitialEndDate,
  impactDashboardDetailedInitialStartDate,
} from './ImpactDashboardDetailedDateFilter';
import { ImpactDashboardDetailedFilters } from './ImpactDashboardDetailedFilters';
import {
  GetImpactDashboardDetailedView,
  GetImpactDashboardDetailedViewVariables,
} from './__generated__/GetImpactDashboardDetailedView';

const useStyles = makeStyles(() =>
  createStyles({
    hideExpandButton: {
      '& td:first-child': {
        '& button': {
          display: 'none',
        },
      },
    },
  })
);

const BASE_COLUMN_NAMES = [
  'reportTypeName',
  'programName',
  'programStatus',
  'activityName',
  'activityStatus',
  'activityDate',
  'submissionDate',
  'submittedBy',
  'attendees',
  'rosterees',
  'startTime',
  'endTime',
  'duration',
  'comments',
];

const defaultMuiDatatableTextLabels = {
  body: {
    noMatch: 'Sorry, no matching records found',
    toolTip: 'Sort',
    columnHeaderTooltip: (column: any) => `Sort for ${column.label}`,
  },
  pagination: {
    next: 'Next Page',
    previous: 'Previous Page',
    rowsPerPage: 'Rows per page:',
    displayRows: 'of',
  },
  toolbar: {
    search: 'Search',
    downloadCsv: 'Download CSV',
    print: 'Print',
    viewColumns: 'View Columns',
    filterTable: 'Filter Table',
  },
  filter: {
    all: 'All',
    title: 'FILTERS',
    reset: 'RESET',
  },
  viewColumns: {
    title: 'Show Columns',
    titleAria: 'Show/Hide Table Columns',
  },
  selectedRows: {
    text: 'row(s) selected',
    delete: 'Delete',
    deleteAria: 'Delete Selected Rows',
  },
};

const ImpactDashboardDetailedView = () => {
  const classes = useStyles();
  const [startDate, setStartDate] = useState<DateTime>(impactDashboardDetailedInitialStartDate);
  const [endDate, setEndDate] = useState<DateTime>(impactDashboardDetailedInitialEndDate);

  const query = useCampfireQuery<GetImpactDashboardDetailedView, GetImpactDashboardDetailedViewVariables>(
    GET_IMPACT_DASHBOARD_DETAILED_VIEW,
    {
      options: {
        variables: {
          startDate: encodeDate(impactDashboardDetailedInitialStartDate),
          endDate: encodeDate(impactDashboardDetailedInitialEndDate),
        },
      },
    }
  );

  useEffect(() => {
    if (!query || !query.refetch) return;
    query.refetch({
      startDate: encodeDate(startDate),
      endDate: encodeDate(endDate),
    });
  }, [startDate, endDate]);

  const filterData = useMemo(() => {
    if (!query.data || !query.data.vm) {
      return {
        programs: [],
        activities: [],
        reportTypes: [],
      };
    }

    const activities = query.data.vm.programs.flatMap((program) =>
      program.activities.map((activity) => ({
        ...activity,
        programId: activity.program.programId,
        sessionReportTypeIds: activity.sessions.flatMap((session) => session.reportType?.reportTypeId ?? ''),
      }))
    );

    const programs = query.data.vm.programs.map((program) => ({
      ...program,
      name: program.dateSuspended === null ? program.name : `${program.name} (Suspended)`,
      activityIds: program.activities.map((activity) => activity.activityId),
      sessionReportTypeIds: program.activities.flatMap((activity) =>
        activity.sessions.map((session) => session.reportType?.reportTypeId ?? '')
      ),
    }));

    const relevantReportTypeIds = activities.flatMap((activity) =>
      activity.activityReports.flatMap((activityReport) =>
        activityReport.sessionReports.flatMap((sessionReport) => sessionReport.session.reportType?.reportTypeId ?? '')
      )
    );
    const relevantReportTypes = query.data.vm.reportTypes.filter((reportType) =>
      relevantReportTypeIds.includes(reportType.reportTypeId)
    );

    const relevantReportTypesSorted = relevantReportTypes.sort((a, b) => a.name.localeCompare(b.name));

    const reportTypeOptions = relevantReportTypeIds.includes('')
      ? relevantReportTypesSorted.concat([
          {
            __typename: 'VOLUNTEER_ReportTypeType',
            reportTypeId: '',
            name: 'Auto-Generating & Self-Directed',
            items: [],
          },
        ])
      : relevantReportTypesSorted;

    return {
      programs: programs.sort((a, b) => a.name.localeCompare(b.name)),
      activities: activities.sort((a, b) => a.name.localeCompare(b.name)),
      reportTypes: reportTypeOptions,
    };
  }, [query.data]);

  const [selectedActivityIds, setSelectedActivityIds] = useState<Array<string>>([]);
  const [selectedProgramIds, setSelectedProgramIds] = useState<Array<string>>([]);
  const [selectedReportTypeIds, setSelectedReportTypeIds] = useState<Array<string>>([]);

  const data = useMemo(() => getTableData(query?.data), [query.data]);
  const columns = useMemo(() => getTableColumns(selectedReportTypeIds, query?.data), [
    query.data,
    selectedReportTypeIds,
  ]);

  const filteredTableData = useMemo(() => {
    return data
      ?.filter(
        (x) =>
          selectedProgramIds.includes(x.programId as string) &&
          selectedActivityIds.includes(x.activityId as string) &&
          selectedReportTypeIds.includes(x.reportTypeId as string)
      )
      .sort((a, b) => (a.activityDate > b.activityDate ? -1 : 1));
  }, [data, selectedProgramIds, selectedActivityIds, selectedReportTypeIds]);

  const tableOptions: MUIDataTableOptions = useMemo(
    () => ({
      elevation: 0,
      print: false,
      responsive: 'standard',
      rowHover: false,
      rowsPerPage: 25,
      rowsPerPageOptions: [25, 50, 100, 250],
      selectableRows: 'none',
      expandableRows: true,
      expandableRowsHeader: false,
      isRowExpandable: (dataIndex: number) => {
        return !!filteredTableData?.[dataIndex]?.hasIndividualReports;
      },
      setRowProps: (_unused: any[], dataIndex: number) => {
        if (!filteredTableData?.[dataIndex]?.hasIndividualReports) {
          return { className: classes.hideExpandButton };
        }
        return {};
      },
      renderExpandableRow: (_unusedRowData: string[], rowMeta: { dataIndex: number }) => {
        const row = filteredTableData?.[rowMeta.dataIndex];
        if (!row) return null;
        const individualReports = (row.individualReports || []) as IndividualSessionReportData[];
        const cicoList = (row.cicoData || []) as CICOData[];
        const allowCICO = !!row.allowCICOFlag;
        const fieldColumns = columns.filter(
          (col) => typeof col === 'object' && 'name' in col && !BASE_COLUMN_NAMES.includes(col.name)
        );

        const getVolunteerCICO = (volunteerId: string) => cicoList.find((c) => c.volunteer.volunteerId === volunteerId);

        return (
          <TableRow>
            <TableCell
              colSpan={columns.length + 1}
              style={{ padding: '12px 24px 16px', backgroundColor: '#f8f9fa', borderBottom: '2px solid #e0e0e0' }}
            >
              <Typography
                variant='subtitle2'
                style={{ fontWeight: 700, fontSize: 13, color: '#555', paddingBottom: 8 }}
              >
                Individual Responses ({individualReports.length})
              </Typography>
              <Box
                style={{ borderRadius: 6, overflow: 'hidden', border: '1px solid #e8e8e8', backgroundColor: '#fff' }}
              >
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f5f5f5' }}>
                      <th
                        style={{
                          textAlign: 'left',
                          padding: '8px 12px',
                          fontWeight: 600,
                          color: '#444',
                          borderBottom: '1px solid #e0e0e0',
                        }}
                      >
                        Volunteer
                      </th>
                      {allowCICO && (
                        <>
                          <th
                            style={{
                              textAlign: 'left',
                              padding: '8px 12px',
                              fontWeight: 600,
                              color: '#444',
                              borderBottom: '1px solid #e0e0e0',
                            }}
                          >
                            Check In
                          </th>
                          <th
                            style={{
                              textAlign: 'left',
                              padding: '8px 12px',
                              fontWeight: 600,
                              color: '#444',
                              borderBottom: '1px solid #e0e0e0',
                            }}
                          >
                            Check Out
                          </th>
                        </>
                      )}
                      {fieldColumns.map((col) => (
                        <th
                          key={typeof col === 'object' && 'name' in col ? col.name : ''}
                          style={{
                            textAlign: 'left',
                            padding: '8px 12px',
                            fontWeight: 600,
                            color: '#444',
                            borderBottom: '1px solid #e0e0e0',
                          }}
                        >
                          {typeof col === 'object' && 'label' in col ? col.label : ''}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {individualReports.map((report, idx) => {
                      const name = `${report.volunteer.profile.preferredName} ${report.volunteer.profile.lastName}`;
                      const initials = `${report.volunteer.profile.preferredName[0] ?? ''}${report.volunteer.profile
                        .lastName[0] ?? ''}`;
                      const cico = getVolunteerCICO(report.volunteer.volunteerId);
                      const isLast = idx === individualReports.length - 1;
                      return (
                        <tr
                          key={report.individualSessionReportId}
                          style={{ borderBottom: isLast ? 'none' : '1px solid #f0f0f0' }}
                        >
                          <td style={{ padding: '8px 12px' }}>
                            <Box display='flex' alignItems='center' style={{ gap: 10 }}>
                              <FallbackAvatar initials={initials} size={28} />
                              <Typography variant='body2' style={{ fontWeight: 500 }}>
                                {name}
                              </Typography>
                            </Box>
                          </td>
                          {allowCICO && (
                            <>
                              <td style={{ padding: '8px 12px' }}>
                                <Typography variant='body2'>
                                  {cico?.checkIn ? unpackToTime(cico.checkIn).toFormat('HH:mm') : '-'}
                                </Typography>
                              </td>
                              <td style={{ padding: '8px 12px' }}>
                                <Typography variant='body2'>
                                  {cico?.checkOut ? unpackToTime(cico.checkOut).toFormat('HH:mm') : '-'}
                                </Typography>
                              </td>
                            </>
                          )}
                          {fieldColumns.map((col) => {
                            const fieldId = typeof col === 'object' && 'name' in col ? col.name : '';
                            const fieldValue = report.fieldValues.find((fv) => fv.field.fieldId === fieldId);
                            return (
                              <td key={fieldId} style={{ padding: '8px 12px' }}>
                                <Typography variant='body2'>
                                  {fieldValue ? getFieldDisplayValue(fieldValue) : ''}
                                </Typography>
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </Box>
            </TableCell>
          </TableRow>
        );
      },
      textLabels: {
        ...defaultMuiDatatableTextLabels,
        body: {
          ...defaultMuiDatatableTextLabels.body,
          noMatch: 'No results - Select a Report Type to get started',
        },
      },
      downloadOptions: {
        filename: `Volaby-${query?.data?.orgName.replace(' ', '_').concat('-') ?? ''}detailed-stats.csv`,
        separator: ',',
      },
      customFooter: (
        count: number,
        page: number,
        rowsPerPage: number,
        changeRowsPerPage: (x: any) => any,
        changePage: (x: number) => void,
        textLabels: any
      ) => {
        return (
          <ImpactDashboardFooter
            count={count}
            page={page}
            rowsPerPage={rowsPerPage}
            changeRowsPerPage={changeRowsPerPage}
            changePage={changePage}
            textLabels={textLabels}
          />
        );
      },
    }),
    [query?.data?.orgName, filteredTableData, columns, classes]
  );

  return (
    <>
      <Box
        paddingTop={3}
        paddingLeft={2}
        paddingRight={2}
        display='flex'
        justifyContent='space-between'
        flexWrap='wrap'
        minHeight={128}
      >
        <ImpactDashboardDetailedDateFilter
          startDate={startDate}
          endDate={endDate}
          setStartDate={setStartDate}
          setEndDate={setEndDate}
        />

        <Box display='flex' flexDirection='column' alignContent='flex-end' alignItems='flex-end'>
          <ImpactDashboardDetailedFilters
            activities={filterData.activities}
            programs={filterData.programs}
            reportTypes={filterData.reportTypes}
            selectedActivityIds={selectedActivityIds}
            selectedProgramIds={selectedProgramIds}
            selectedReportTypeIds={selectedReportTypeIds}
            setSelectedActivityIds={setSelectedActivityIds}
            setSelectedProgramIds={setSelectedProgramIds}
            setSelectedReportTypeIds={setSelectedReportTypeIds}
          />

          <Typography style={{ paddingTop: 4, paddingRight: 8 }} variant='body2' color='textSecondary'>
            {`${filteredTableData?.length} results (excluding extra filters)`}
          </Typography>
        </Box>
      </Box>

      {filteredTableData && filteredTableData.length > 0 ? (
        <MUIDataTable options={tableOptions} title='' columns={columns} data={filteredTableData} />
      ) : (
        <Box width={1} height={360} display='flex' alignItems='center' alignContent='center' justifyContent='center'>
          <Box textAlign='center'>
            <Typography variant='h5' color='textSecondary'>
              {'No Results'}
            </Typography>
            <Typography variant='body1' color='textSecondary'>
              {selectedReportTypeIds.length === 0 && filterData.reportTypes.length > 0
                ? 'Select a Report type to get started'
                : 'Try a different time period'}
            </Typography>
          </Box>
        </Box>
      )}

      <CircularProgressOverlay isLoading={query.loading} />
    </>
  );
};

export { ImpactDashboardDetailedView };
